import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from '@hooks/useTranslation';

interface ComposerProps {
  onSend: (text: string) => void;
  disabled?: boolean;
}

/** Sprint 32, story-165. Extracted from `AiPlanGenerator/ChatPanel.tsx`'s textarea + send — Enter
 * sends, Shift+Enter inserts a newline, disabled only while `THINKING` (stays enabled while an
 * action card is showing, per D6). */
export const Composer: React.FC<ComposerProps> = ({ onSend, disabled }) => {
  const t = useTranslation('assistant');
  const [value, setValue] = useState('');
  const inputRef = useRef<HTMLTextAreaElement>(null);

  // Focus lands in the input on page open and whenever it becomes usable again (reply arrived).
  useEffect(() => {
    if (!disabled) inputRef.current?.focus();
  }, [disabled]);

  const handleSend = () => {
    if (!value.trim() || disabled) return;
    onSend(value.trim());
    setValue('');
  };

  return (
    <div className="mt-3 flex gap-2" data-testid="assistant-composer">
      <textarea
        ref={inputRef}
        rows={2}
        value={value}
        disabled={disabled}
        onChange={(e) => setValue(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault();
            handleSend();
          }
        }}
        placeholder={t.chatPlaceholder}
        data-testid="assistant-composer-input"
        className="flex-1 text-sm rounded-lg border border-gray-200 dark:border-gray-700 bg-transparent p-2 resize-none disabled:opacity-60"
      />
      <button
        type="button"
        disabled={disabled || !value.trim()}
        onClick={handleSend}
        data-testid="assistant-composer-send"
        className="px-3 py-1 h-fit rounded-lg text-xs font-medium bg-primary text-white disabled:opacity-60">
        {t.send}
      </button>
    </div>
  );
};

export default Composer;
